import { inject, Injectable } from '@angular/core';
import {
  BIPApiResponsePaginated,
  BIPQueryParams,
  OrgUserQueryParams,
  UserApiResponsePaginated,
} from './insights.interface';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  Observable,
  tap,
  catchError,
  BehaviorSubject,
  of,
  finalize,
  share,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';
import { ApiRespones } from '../settings/settings.interface';
import { ApiResponse } from '../../organizations/organization.interface';
import {
  UploadEvent,
  UploadProgressService,
} from '../../../core/services/upload-progress.service';

@Injectable({
  providedIn: 'root',
})
export class InsightsService {
  private readonly uploadProgressService = inject(UploadProgressService);
  private readonly API_URL = `${environment.apiUrl}/Student`;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly insightStudentsSubject =
    new BehaviorSubject<UserApiResponsePaginated | null>(null);
  organizationUsers$ = this.insightStudentsSubject.asObservable();

  private readonly BIPSubject =
    new BehaviorSubject<BIPApiResponsePaginated | null>(null);
  BIP$ = this.BIPSubject.asObservable();

  private readonly activeStudentsRequests = new Map<
    string,
    Observable<UserApiResponsePaginated>
  >();
  private lastStudentsRequestTime = 0;
  private readonly DEBOUNCE_TIME = 300; // ms

  private readonly activeBIPRequests = new Map<
    string,
    Observable<BIPApiResponsePaginated>
  >();
  private lastBIPRequestTime = 0;

  private buildUserQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    searchTerm,
    organizationId,
  }: OrgUserQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }

    if (sort) {
      params = params.set('sort', sort);
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }

    return params;
  }

  getOrgStudents(
    queryParams: OrgUserQueryParams
  ): Observable<UserApiResponsePaginated> {
    const params = this.buildUserQueryParams(queryParams);
    const requestKey = params.toString();
    const now = Date.now();

    if (now - this.lastStudentsRequestTime < this.DEBOUNCE_TIME) {
      const currentValue = this.insightStudentsSubject.getValue();
      if (currentValue) {
        return of(currentValue);
      }
    }

    this.lastStudentsRequestTime = now;

    const activeRequest = this.activeStudentsRequests.get(requestKey);
    if (activeRequest) {
      return activeRequest;
    }

    const request = this.http
      .get<UserApiResponsePaginated>(`${this.API_URL}/list`, { params })
      .pipe(
        tap(response => this.insightStudentsSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error)),
        share(),
        finalize(() => {
          this.activeStudentsRequests.delete(requestKey);
        })
      );
    this.activeStudentsRequests.set(requestKey, request);

    return request;
  }

  deleteStudent(id: number): Observable<ApiRespones<void>> {
    return this.http
      .delete<ApiRespones<void>>(`${this.API_URL}/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private buildBIPQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    searchTerm,
    organizationId,
    statusId,
    IsInsightAdminTab,
  }: BIPQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }

    if (sort) {
      params = params.set('sort', sort);
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (statusId) {
      params = params.set('statusId', statusId);
    }

    if (organizationId) {
      params = params.set('OrganizationId', organizationId.toString());
    }
    if (IsInsightAdminTab !== undefined) {
      params = params.set('IsInsightAdminTab', IsInsightAdminTab.toString());
    }
    return params;
  }

  getBIPList(queryParams: BIPQueryParams): Observable<BIPApiResponsePaginated> {
    const params = this.buildBIPQueryParams(queryParams);
    const requestKey = params.toString();
    const now = Date.now();

    if (now - this.lastBIPRequestTime < this.DEBOUNCE_TIME) {
      const currentValue = this.BIPSubject.getValue();
      if (currentValue) {
        return of(currentValue);
      }
    }

    this.lastBIPRequestTime = now;

    const activeRequest = this.activeBIPRequests.get(requestKey);
    if (activeRequest) {
      return activeRequest;
    }

    const request = this.http
      .get<BIPApiResponsePaginated>(
        `${environment.apiUrl}/Insight/list-bip-case`,
        {
          params,
        }
      )
      .pipe(
        tap(response => this.BIPSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error)),
        share(),
        finalize(() => {
          this.activeBIPRequests.delete(requestKey);
        })
      );

    this.activeBIPRequests.set(requestKey, request);

    return request;
  }

  bulkCreateUsers(
    file: File,
    organizationId: number
  ): Observable<UploadEvent<void>> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new HttpParams().set(
      'organizationId',
      organizationId.toString()
    );
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/import-student`, formData, { params, reportProgress: true, observe: 'events' })
      .pipe(
        this.uploadProgressService.getUploadProgress<void>(),
        tap(response => {
          if (response.status === 'completed') {
            this.getOrgStudents({
              pageIndex: 1,
              pageSize: 10,
              organizationId: organizationId,
            }).subscribe();
          }
        })
      );
  }
  updateCaseStatus(caseId: number, statusId: number) {
    return this.http
      .post<
        ApiRespones<void>
      >(`${environment.apiUrl}/Insight/update-case-status?caseId=${caseId}&statusId=${statusId}`, {})
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
